import { createSlice } from '@reduxjs/toolkit';
import { EMPTY_ARRAY } from '../../../constants/productConfiguratorConstants';
import {
  addItemsToMapList,
  removeItemsFromMapList,
  updateItemInMapList,
  updateItemsInMapList,
} from '../../../helpers/reduxHelper';
import { selectSelectedSceneId, selectSelectedViewId } from './configurator.slicer';

function defaultViews(state, sceneId, loading = false, loaded = false) {
  if (state.data[sceneId]) return;
  state.data[sceneId] = {
    loading,
    loaded,
    error: false,
    views: [],
    selectedViewInScene: {},
  };
}

const getInitialState = () => ({
  data: {},
});

const configuratorSceneViews = createSlice({
  name: 'configuratorSceneViews',
  initialState: getInitialState(),
  reducers: {
    resetState: () => {
      return getInitialState();
    },
    fetchSceneViewsStart: (state, { payload }) => {
      const { sceneId } = payload;
      defaultViews(state, sceneId, true, false);
      state.data[sceneId].loading = true;
      state.data[sceneId].loaded = false;
      state.data[sceneId].error = false;
      state.data[sceneId].views = [];
    },
    fetchSceneViewsSuccess: (state, { payload }) => {
      const { sceneId, views } = payload;
      defaultViews(state, sceneId);
      state.data[sceneId].loading = false;
      state.data[sceneId].loaded = true;
      state.data[sceneId].error = false;
      state.data[sceneId].views = views;
    },
    fetchSceneViewsFailed: (state, { payload }) => {
      const { sceneId } = payload;
      defaultViews(state, sceneId);
      state.data[sceneId].loading = false;
      state.data[sceneId].loaded = true;
      state.data[sceneId].error = true;
      state.data[sceneId].views = [];
    },
    setSelectedViewInScene: (state, { payload }) => {
      const { sceneId, view } = payload;
      defaultViews(state, sceneId);
      state.data[sceneId].selectedViewInScene = view;
    },
    addViewsToScene: (state, action) => {
      defaultViews(state, action.payload.sceneId);
      addItemsToMapList('views', 'sceneId', 'views', 'data')(state, action);
    },
    removeViewsFromScene: removeItemsFromMapList('views', 'sceneId', 'data'),
    updateView: updateItemInMapList('views', 'sceneId', 'view', 'data'),
    deleteViews: removeItemsFromMapList('views', 'sceneId', 'data'),
    updateViews: updateItemsInMapList('views', 'sceneId', 'views', 'data'),
    selectLighting: (state, { payload }) => {
      const { sceneId, viewId, lightingId } = payload;
      state.data[sceneId].views.find((v) => v.id === viewId).lightingId = lightingId;
    },
  },
});

export const configuratorSceneViewsActions = configuratorSceneViews.actions;
export const configuratorSceneViewsReducer = configuratorSceneViews.reducer;

export const selectFailedToLoadViews = (sceneId) => (state) => {
  const s = state.configuratorSceneViews.data[sceneId];
  return Boolean(s) && s.error;
};

export const selectSelectedViewInScene = (sceneId) => (state) => {
  return state.configuratorSceneViews.data[sceneId]?.selectedViewInScene;
};

export const selectSceneViews =
  (sceneId, { active } = {}) =>
  (state) => {
    const views = state.configuratorSceneViews.data[sceneId]?.views || EMPTY_ARRAY;
    return views;
  };

export const selectCurrentSceneViews = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneViews(sceneId)(state);
};

export const selectSceneViewsLoaded = (sceneId) => (state) => {
  return state.configuratorSceneViews.data[sceneId]?.loaded;
};

export const selectCurrentSceneViewsLoaded = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneViewsLoaded(sceneId)(state);
};

export const selectSceneView = (sceneId, viewId) => (state) => {
  return selectSceneViews(sceneId)(state)?.find((v) => v.id === viewId);
};

export const selectSceneViewsLoading = (sceneId) => (state) => {
  return state.configuratorSceneViews.data[sceneId]?.loading;
};

export const selectCurrentSceneViewsLoading = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneViewsLoading(sceneId)(state);
};

export const selectSceneHasViews = (sceneId) => (state) => {
  return selectSceneViews.length > 0;
};

export const selectLightingId = (state) => {
  const sceneId = selectSelectedSceneId(state);
  const viewId = selectSelectedViewId(state);
  return selectSceneViews(sceneId)(state).find((v) => v.id === viewId)?.lightroom;
};

export const selectViewDetailsIfBackgroundAppliedToAll = (state) => {
  const sceneId = selectSelectedSceneId(state);
  return selectSceneViews(sceneId)(state).find((v) => v.is_bg && v.applyToAllViews);
};

export const selectSelectedView = (state) => {
  const viewId = selectSelectedViewId(state);
  const sceneId = selectSelectedSceneId(state);
  return selectSceneViews(sceneId)(state).find((view) => view.id === viewId);
};
